import { Button } from '@platform/components';
import {
  ProfileItem,
  ProfileItemLong,
  ProfileSection,
  ProfileTabContainer,
} from '../../components/ProfileComponents';
import { ProjectSuggestions } from '@platform/client';
import { useState } from 'react';
import { SuggestProjectModal } from './SuggestProjectModal';
import { ProjectSuggestion, SuggestionDateStatus } from '@platform/client/schemas';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { GenerateNewSuggestionsModal } from './NewSuggestionsModal';
import { businessChallengeFullText } from '../../../util/TextHelpers';
import { Link } from 'wouter';
import { CreateProjectModal } from './CreateProjectModal';

export const ProjectSuggestionTabContent = ({ organizationId }: { organizationId: number }) => {
  const [suggestProjectData, setSuggestProjectData] = useState<{
    id: number;
    index: number;
  }>();
  const [createProjectData, setCreateProjectData] = useState<{
    id: number;
    index: number;
  }>();
  const [confirmNewSuggestionsModalOpen, setConfirmNewSuggestionsModalOpen] = useState(false);
  const {
    data: projectSuggestionData,
    isLoading,
    isError,
    refetch,
  } = ProjectSuggestions.useGetProjectSuggestions(organizationId);
  const generateNewSuggestions = ProjectSuggestions.useGenerateOrgProjectSuggestions({
    mutation: {
      onSuccess: () => refetch(),
    },
  });
  const hasExistingSuggestions = (projectSuggestionData?.projectSuggestions?.length ?? 0) > 0;
  return (
    <ProfileTabContainer>
      <div className='flex gap-2 items-baseline'>
        {!hasExistingSuggestions && (
          <Button
            variant='secondary-color'
            size='sm'
            isDisabled={isLoading || generateNewSuggestions.isPending}
            onPress={() => generateNewSuggestions.mutate({ organizationId })}
          >
            Generate suggested projects
          </Button>
        )}
        {!!hasExistingSuggestions && (
          <Button
            variant='primary'
            size='sm'
            onPress={() => setConfirmNewSuggestionsModalOpen(true)}
            isDisabled={isLoading || generateNewSuggestions.isPending}
          >
            Generate new suggestions
          </Button>
        )}
        {(isLoading || generateNewSuggestions.isPending) && (
          <p>Loading suggestions... this may take a few minutes</p>
        )}
        {(isError || generateNewSuggestions.isError) && (
          <p className='text-red-500'>Error loading suggestions. Please try again.</p>
        )}
      </div>
      <div className='mt-4'>
        {hasExistingSuggestions && (
          <div className=''>
            {projectSuggestionData?.projectSuggestions.map((suggestion, index) => (
              <ProjectSuggestionSection
                key={index}
                index={index}
                setSuggestProjectData={setSuggestProjectData}
                setCreateProjectData={setCreateProjectData}
                suggestedProject={suggestion}
              />
            ))}
          </div>
        )}
        {!generateNewSuggestions.isPending && <p>No project suggestions generated.</p>}
      </div>
      {suggestProjectData && (
        <SuggestProjectModal
          organizationId={organizationId}
          projectSuggestionId={suggestProjectData.id}
          projectSuggestionIndex={suggestProjectData.index}
          onClose={() => setSuggestProjectData(undefined)}
        />
      )}
      <GenerateNewSuggestionsModal
        organizationId={organizationId}
        generateNewSuggestions={generateNewSuggestions}
        isOpen={confirmNewSuggestionsModalOpen}
        onClose={() => setConfirmNewSuggestionsModalOpen(false)}
      />
      {createProjectData && (
        <CreateProjectModal
          organizationId={organizationId}
          projectSuggestionId={createProjectData?.id}
          projectSuggestionIndex={createProjectData?.index}
          onClose={() => setCreateProjectData(undefined)}
        />
      )}
    </ProfileTabContainer>
  );
};

const ProjectSuggestionSection = ({
  index,
  setSuggestProjectData,
  setCreateProjectData,
  suggestedProject,
}: {
  index: number;
  setSuggestProjectData: React.Dispatch<
    React.SetStateAction<{ id: number; index: number } | undefined>
  >;
  setCreateProjectData: React.Dispatch<
    React.SetStateAction<{ id: number; index: number } | undefined>
  >;
  suggestedProject: ProjectSuggestion;
}) => {
  return (
    <ProfileSection key={index} columns={1}>
      <div className='flex items-baseline justify-between mb-2'>
        <h3 className='text-lg font-semibold'>
          Suggestion #{index + 1} <span className='text-xs'>[id: {suggestedProject.id}]</span>
        </h3>
        {!suggestedProject.projectId && (
          <div className='flex gap-2 items-baseline'>
            {suggestedProject.suggestedDate && (
              <SuggestionEmailString
                status={suggestedProject.suggestedDate.status}
                suggestedDate={suggestedProject.suggestedDate.date}
              />
            )}
            <Button
              size='sm'
              variant='secondary-gray'
              onClick={() => setSuggestProjectData({ id: suggestedProject.id, index })}
            >
              {suggestedProject.suggestedDate ?
                suggestedProject.suggestedDate.status == 'sent' ?
                  'Resend'
                : 'Reschedule'
              : 'Email'}{' '}
              project suggestion
            </Button>
            {!suggestedProject.projectId && (
              <Button
                size='sm'
                variant='secondary-gray'
                onClick={() => setCreateProjectData({ id: suggestedProject.id, index })}
              >
                Create project
              </Button>
            )}
          </div>
        )}
      </div>
      {suggestedProject.projectId ?
        <Link className='blue-link' to={`~/platform/admin/projects/${suggestedProject.projectId}`}>
          {suggestedProject.title}
        </Link>
      : <ProfileItem label='Title' value={suggestedProject.title} />}
      <ProfileItem
        label='Deliverables'
        value={<DeliverablesList deliverables={suggestedProject.deliverables} />}
      />
      <ProfileItem label='Skills' value={suggestedProject.skills.join(', ')} />
      <ProfileItem label='Background' value={suggestedProject.background} />
      <ProfileItem label='Learning Outcomes' value={suggestedProject.learningOutcomes} />
      <ProfileItem
        label='Business Challenge'
        value={businessChallengeFullText(suggestedProject.businessChallenge)}
      />
      <ProfileItemLong label='Challenge Context' value={suggestedProject.challengeContext} />
      <ProfileItem label='Focus' value={suggestedProject.focus} />
    </ProfileSection>
  );
};

const SuggestionEmailString = ({
  status,
  suggestedDate,
}: {
  status: SuggestionDateStatus;
  suggestedDate: string;
}) => {
  const dateAsDate = new Date(suggestedDate + 'Z');
  const todayAsDate = new Date();
  const isPast = dateAsDate <= todayAsDate;
  console.log('dateAsDate', dateAsDate, 'todayAsDate', todayAsDate, 'isPast', isPast);
  return (
    <p className='text-xs text-gray-400'>
      ({status === 'sent' ? 'Emailed' : 'Scheduled to be emailed'} {formatDateStr(suggestedDate)})
    </p>
  );
};

const DeliverablesList = ({ deliverables }: { deliverables: string[] }) => {
  return (
    <ul className='list-disc pl-3'>
      {deliverables.map((deliverable, index) => (
        <li className='text-gray-700 font-medium text-sm' key={index}>
          {deliverable}
        </li>
      ))}
    </ul>
  );
};
